/**
 * useSimulationSession — main state machine hook for the Simulador.
 *
 * Owns:
 *   - The discriminated `SimulatorViewState` (setup → starting → chat → ended).
 *   - Lifecycle calls: start, reply (with optimistic append), end, resume.
 *   - On-mount resume: looks for an active|paused session via Supabase RLS,
 *     then calls /resume to rehydrate transcript.
 *
 * Tokens are pulled from Clerk via `useSession().session?.getToken()`,
 * matching the pattern in `useGenerateRoteiro`.
 *
 * Errors are surfaced via `toast` (sonner). Optimistic user messages are
 * rolled back if the /reply call fails.
 *
 * Spec: docs/architecture/simulador-spec.md §5.2 / §5.3.
 */
import { useCallback, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { toastError } from "@/lib/userMessages";
import { supabase } from "@/lib/supabase";
import {
  endSession,
  findActiveSession,
  replyTurn,
  resumeSession,
  startSession,
} from "../api/client";
import type {
  SimulatorDifficulty,
  SimulatorMessage,
  SimulatorViewState,
} from "../types";

interface UseSimulationSessionResult {
  view: SimulatorViewState;
  isStarting: boolean;
  isReplying: boolean;
  isResuming: boolean;
  start: (
    eneatype: number,
    difficulty: SimulatorDifficulty,
    procedureId?: string | null,
  ) => Promise<void>;
  reply: (content: string, opts?: { audio_url?: string }) => Promise<void>;
  end: (reason?: "user_quit" | "timeout" | "admin") => Promise<void>;
  resumeIfActive: () => Promise<void>;
  resume: (sessionId: string) => Promise<void>;
  reset: () => void;
  /**
   * Append a hint or system-style message into the active chat view.
   * Used by `useSimulatorHint` after the server-side hint insert so the
   * coach bubble appears inline with the conversation.
   */
  appendLocalMessage: (msg: SimulatorMessage) => void;
}

export function useSimulationSession(): UseSimulationSessionResult {
  const { session } = useSession();
  const [view, setViewState] = useState<SimulatorViewState>({ kind: "setup" });
  const [isStarting, setIsStarting] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  // Mirror view in a ref so async paths can read current state without
  // racing React's batched updates.
  const viewRef = useRef<SimulatorViewState>(view);
  const setView = useCallback(
    (next: SimulatorViewState | ((prev: SimulatorViewState) => SimulatorViewState)) => {
      setViewState((prev) => {
        const resolved =
          typeof next === "function"
            ? (next as (p: SimulatorViewState) => SimulatorViewState)(prev)
            : next;
        viewRef.current = resolved;
        return resolved;
      });
    },
    [],
  );

  // Avoid double mount-time resume in React strict mode.
  const resumedOnce = useRef(false);

  const getToken = useCallback(async (): Promise<string> => {
    const t = await session?.getToken();
    if (!t) throw new Error("Usuário não autenticado.");
    return t;
  }, [session]);

  const reset = useCallback(() => {
    setView({ kind: "setup" });
  }, [setView]);

  const start = useCallback(
    async (
      eneatype: number,
      difficulty: SimulatorDifficulty,
      procedureId?: string | null,
    ) => {
      setIsStarting(true);
      setView({ kind: "starting", eneatype, difficulty });
      try {
        const token = await getToken();
        const res = await startSession(
          {
            eneatype,
            difficulty,
            procedure_id: procedureId ?? undefined,
          },
          token,
        );
        const firstMessage: SimulatorMessage = {
          seq: res.first_message.seq,
          role: "patient",
          content: res.first_message.content,
          created_at: res.first_message.created_at,
        };
        setView({
          kind: "chat",
          sessionId: res.session_id,
          persona: res.persona,
          procedure: res.procedure,
          difficulty,
          messages: [firstMessage],
          lastSeq: firstMessage.seq,
          isPatientTyping: false,
        });
      } catch (err) {
        toastError(err, "Não foi possível iniciar a simulação. Tente novamente.");
        setView({ kind: "setup" });
      } finally {
        setIsStarting(false);
      }
    },
    [getToken, setView],
  );

  const reply = useCallback(
    async (content: string, opts?: { audio_url?: string }) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      const current = viewRef.current;
      if (current.kind !== "chat") return;

      const sessionId = current.sessionId;
      // client_seq for optimistic-lock = server's last_seq (before our user msg).
      const clientSeq = current.lastSeq;
      const optimisticSeq = current.lastSeq + 1;
      const optimistic: SimulatorMessage = {
        seq: optimisticSeq,
        role: "user",
        content: trimmed,
        audio_url: opts?.audio_url ?? null,
        created_at: new Date().toISOString(),
      };
      setView({
        ...current,
        messages: [...current.messages, optimistic],
        lastSeq: optimisticSeq,
        isPatientTyping: true,
      });

      setIsReplying(true);
      try {
        const token = await getToken();
        const res = await replyTurn(
          {
            session_id: sessionId,
            content: trimmed,
            client_seq: clientSeq,
            audio_url: opts?.audio_url,
          },
          token,
        );

        const patientMsg: SimulatorMessage = {
          seq: res.patient_message.seq,
          role: "patient",
          content: res.patient_message.content,
          created_at: res.patient_message.created_at,
        };

        setView((prev) => {
          if (prev.kind !== "chat") return prev;
          return {
            ...prev,
            messages: [...prev.messages, patientMsg],
            lastSeq: res.last_seq,
            isPatientTyping: false,
          };
        });

        // Terminal verdicts keep us in chat with overlay; user navigates manually.
        // (No state transition here — UI checks for terminal status downstream.)
        if (res.status === "close_won" || res.status === "close_lost") {
          // Stash terminal status by tagging classifier_meta on patient msg.
          setView((prev) => {
            if (prev.kind !== "chat") return prev;
            const msgs = prev.messages.map((m) =>
              m.seq === patientMsg.seq
                ? { ...m, classifier_meta: { status: res.status, verdict: res.close_verdict } }
                : m,
            );
            return { ...prev, messages: msgs };
          });
        }
      } catch (err) {
        // Roll back optimistic message.
        setView((prev) => {
          if (prev.kind !== "chat") return prev;
          return {
            ...prev,
            messages: prev.messages.filter((m) => m.seq !== optimisticSeq),
            lastSeq: Math.max(0, prev.lastSeq - 1),
            isPatientTyping: false,
          };
        });
        toastError(err, "Não foi possível enviar a mensagem. Tente novamente.");
      } finally {
        setIsReplying(false);
      }
    },
    [getToken, setView],
  );

  const end = useCallback(
    async (reason: "user_quit" | "timeout" | "admin" = "user_quit") => {
      const current = viewRef.current;
      const sessionId = current.kind === "chat" ? current.sessionId : "";
      if (!sessionId) {
        setView({ kind: "setup" });
        return;
      }
      try {
        const token = await getToken();
        await endSession({ session_id: sessionId, reason }, token);
        setView({ kind: "ended", sessionId });
      } catch (err) {
        toastError(err, "Não foi possível encerrar o atendimento. Tente novamente.");
      }
    },
    [getToken, setView],
  );

  const resumeSessionById = useCallback(async (sessionId: string) => {
    setIsResuming(true);
    try {
      const token = await getToken();
      const res = await resumeSession({ session_id: sessionId, since_seq: 0 }, token);
      // If session is already terminal/expired, push to ended view instead.
      if (
        res.session.status === "close_won" ||
        res.session.status === "close_lost" ||
        res.session.status === "ended" ||
        res.session.status === "abandoned" ||
        res.session.status === "expired"
      ) {
        setView({ kind: "ended", sessionId });
        return;
      }
      setView({
        kind: "chat",
        sessionId,
        persona: res.persona,
        procedure: res.procedure,
        difficulty: res.session.difficulty,
        messages: res.messages,
        lastSeq: res.last_seq,
        isPatientTyping: false,
      });
    } catch (err) {
      console.error("[simulador] resume failed:", err);
      toastError(err, "Não foi possível retomar o atendimento. Tente novamente.");
      setView({ kind: "setup" });
    } finally {
      setIsResuming(false);
    }
  }, [getToken, setView]);

  const resumeIfActive = useCallback(async () => {
    if (resumedOnce.current) return;
    resumedOnce.current = true;
    try {
      const sessionId = await findActiveSession(supabase);
      if (!sessionId) return;
      await resumeSessionById(sessionId);
    } catch (err) {
      console.error("[simulador] active-session lookup failed:", err);
    }
  }, [resumeSessionById]);

  const appendLocalMessage = useCallback(
    (msg: SimulatorMessage) => {
      setView((prev) => {
        if (prev.kind !== "chat") return prev;
        // De-dupe by seq so repeated calls (e.g., refresh + append) don't double.
        if (prev.messages.some((m) => m.seq === msg.seq)) return prev;
        return {
          ...prev,
          messages: [...prev.messages, msg],
          lastSeq: Math.max(prev.lastSeq, msg.seq),
        };
      });
    },
    [setView],
  );

  return {
    view,
    isStarting,
    isReplying,
    isResuming,
    start,
    reply,
    end,
    resumeIfActive,
    resume: resumeSessionById,
    reset,
    appendLocalMessage,
  };
}
